import { Link } from 'react-router-dom'
import { useState } from 'react'
import { Facebook, Youtube, Instagram, MapPin, Phone, Mail, Clock, Zap, Shield, Truck, HeartHandshake, Send, Bike } from 'lucide-react'
import { useSettings } from '@/modules/settings-api'

export function Footer() {
  const { data: s } = useSettings()
  const year = new Date().getFullYear()
  const [email, setEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setSubscribed(true)
    setEmail('')
    setTimeout(() => setSubscribed(false), 3000)
  }

  return (
    <footer className="mt-20 bg-gradient-to-b from-[#0f172a] to-[#020617] text-slate-300 relative">
      {/* Top strip — commitments */}
      <div className="border-b border-white/5 bg-white/[0.02]">
        <div className="max-w-[1440px] mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-5">
          {[
            { Icon: Truck,         title: 'Giao hàng toàn quốc',    desc: 'Miễn phí đơn từ ' + ((s.freeShippingThreshold ?? 5000000) / 1000000) + ' triệu' },
            { Icon: Shield,        title: 'Bảo hành ' + (s.warrantyMonths ?? 36) + ' tháng', desc: 'Chính hãng 1-1' },
            { Icon: Zap,           title: 'Pin Lithium cao cấp',   desc: 'Đi xa 70-100km/sạc' },
            { Icon: HeartHandshake,title: 'Hỗ trợ trọn đời',       desc: 'Hotline 24/7' },
          ].map(({ Icon, title, desc }, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-brand-600/20 text-brand-400 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white truncate">{title}</div>
                <div className="text-xs text-slate-400 truncate">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main grid */}
      <div className="max-w-[1440px] mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Brand + newsletter */}
        <div className="md:col-span-4">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-4">
            <div className="w-11 h-11 rounded-xl bg-brand-600 text-white flex items-center justify-center">
              <Bike className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xl font-bold text-white">{s.shopName}</div>
              <div className="text-[11px] text-brand-400 font-medium -mt-0.5">Di chuyển xanh — Cuộc sống đẹp hơn</div>
            </div>
          </Link>
          <p className="text-sm leading-relaxed text-slate-400 mb-5">
            {s.description || 'Chuyên cung cấp xe máy điện, xe đạp điện chính hãng — bảo hành dài hạn, giao hàng toàn quốc, lắp đặt tận nơi.'}
          </p>

          {/* Newsletter */}
          <div>
            <div className="text-xs font-semibold text-white uppercase tracking-wider mb-2.5">Nhận ưu đãi mới nhất</div>
            {subscribed ? (
              <div className="rounded-md bg-accent-500/15 border border-accent-500/30 text-accent-400 px-3 py-2.5 text-sm">
                Cảm ơn bạn đã đăng ký!
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email của bạn"
                  className="flex-1 h-10 rounded-md bg-white/5 border border-white/10 px-3 text-sm text-white placeholder:text-slate-500 outline-none focus:border-brand-500 transition"
                />
                <button type="submit" className="h-10 w-10 rounded-md bg-brand-600 hover:bg-brand-500 text-white flex items-center justify-center transition">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>

          {/* Social */}
          <div className="flex items-center gap-2.5 mt-5">
            {s.facebookUrl && <a href={s.facebookUrl} target="_blank" rel="noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#1877F2] text-slate-400 hover:text-white flex items-center justify-center transition"><Facebook className="w-4 h-4" /></a>}
            {s.youtubeUrl && <a href={s.youtubeUrl} target="_blank" rel="noreferrer" aria-label="YouTube" className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#FF0000] text-slate-400 hover:text-white flex items-center justify-center transition"><Youtube className="w-4 h-4" /></a>}
            {s.instagramUrl && <a href={s.instagramUrl} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full bg-white/5 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 text-slate-400 hover:text-white flex items-center justify-center transition"><Instagram className="w-4 h-4" /></a>}
            {s.zaloPhone && <a href={`https://zalo.me/${s.zaloPhone}`} target="_blank" rel="noreferrer" aria-label="Zalo" className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#0068FF] text-slate-400 hover:text-white flex items-center justify-center transition text-xs font-bold">Z</a>}
          </div>
        </div>

        {/* Products */}
        <div className="md:col-span-2">
          <div className="text-white font-semibold mb-4">Sản phẩm</div>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/danh-muc/xe-may-dien" className="text-slate-400 hover:text-white transition">Xe máy điện</Link></li>
            <li><Link to="/danh-muc/xe-dap-dien" className="text-slate-400 hover:text-white transition">Xe đạp điện</Link></li>
            <li><Link to="/danh-muc/phu-kien" className="text-slate-400 hover:text-white transition">Phụ kiện</Link></li>
            <li><Link to="/danh-muc/pin-sac" className="text-slate-400 hover:text-white transition">Pin & Sạc</Link></li>
            <li><Link to="/khuyen-mai" className="text-brand-400 hover:text-brand-300 transition font-medium">Khuyến mãi hot →</Link></li>
          </ul>
        </div>

        {/* Support */}
        <div className="md:col-span-2">
          <div className="text-white font-semibold mb-4">Hỗ trợ</div>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/chinh-sach-bao-hanh" className="text-slate-400 hover:text-white transition">Chính sách bảo hành</Link></li>
            <li><Link to="/chinh-sach-doi-tra" className="text-slate-400 hover:text-white transition">Đổi trả {s.returnPolicyDays} ngày</Link></li>
            <li><Link to="/huong-dan-mua-hang" className="text-slate-400 hover:text-white transition">Hướng dẫn mua hàng</Link></li>
            <li><Link to="/chinh-sach-giao-hang" className="text-slate-400 hover:text-white transition">Chính sách giao hàng</Link></li>
            <li><Link to="/cau-hoi-thuong-gap" className="text-slate-400 hover:text-white transition">Câu hỏi thường gặp</Link></li>
          </ul>
        </div>

        {/* About */}
        <div className="md:col-span-2">
          <div className="text-white font-semibold mb-4">Về chúng tôi</div>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/gioi-thieu" className="text-slate-400 hover:text-white transition">Giới thiệu</Link></li>
            <li><Link to="/he-thong-cua-hang" className="text-slate-400 hover:text-white transition">Hệ thống cửa hàng</Link></li>
            <li><Link to="/tuyen-dung" className="text-slate-400 hover:text-white transition">Tuyển dụng</Link></li>
            <li><Link to="/tin-tuc" className="text-slate-400 hover:text-white transition">Tin tức</Link></li>
            <li><Link to="/lien-he" className="text-slate-400 hover:text-white transition">Liên hệ</Link></li>
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-2">
          <div className="text-white font-semibold mb-4">Liên hệ</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2.5">
              <MapPin className="w-4 h-4 mt-0.5 text-brand-500 shrink-0" />
              <span className="text-slate-400">{s.address || '—'}</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Phone className="w-4 h-4 mt-0.5 text-brand-500 shrink-0" />
              <a href={`tel:${(s.hotline ?? '').replace(/\s/g, '')}`} className="text-white font-semibold hover:text-brand-400 transition">{s.hotline}</a>
            </li>
            <li className="flex items-start gap-2.5">
              <Mail className="w-4 h-4 mt-0.5 text-brand-500 shrink-0" />
              <a href={`mailto:${s.email}`} className="text-slate-400 hover:text-white transition break-all">{s.email}</a>
            </li>
            <li className="flex items-start gap-2.5">
              <Clock className="w-4 h-4 mt-0.5 text-brand-500 shrink-0" />
              <span className="text-slate-400">{s.workingHours || 'Thứ 2 — Chủ nhật: 8:00 — 20:00'}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Payment + bottom */}
      <div className="border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-slate-500 uppercase tracking-wider">Thanh toán:</span>
            <div className="flex items-center gap-2">
              <PayBadge label="COD" />
              <PayBadge label="QR" />
              <PayBadge label="Vietcombank" />
              <PayBadge label="Momo" />
              <PayBadge label="VNPay" />
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <img src="https://images.dmca.com/Badges/dmca-badge-w100-5x1-04.png" alt="DMCA" className="h-5 opacity-70" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
            <span>Đã đăng ký Bộ Công Thương</span>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5 py-5 bg-black/30">
        <div className="max-w-[1440px] mx-auto px-6 flex items-center justify-between gap-4 flex-wrap text-xs text-slate-500">
          <div>© {year} <span className="text-slate-300 font-medium">{s.shopName}</span>. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <Link to="/dieu-khoan" className="hover:text-white transition">Điều khoản sử dụng</Link>
            <span>•</span>
            <Link to="/chinh-sach-bao-mat" className="hover:text-white transition">Chính sách bảo mật</Link>
            <span>•</span>
            <Link to="/sitemap" className="hover:text-white transition">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

function PayBadge({ label }: { label: string }) {
  return (
    <span className="px-2.5 h-7 rounded-md bg-white/5 border border-white/10 text-xs text-slate-300 font-medium inline-flex items-center">
      {label}
    </span>
  )
}
